import type { V2 } from '../core/math';
import { obbAxes, pointSegmentDistance } from '../world/geom';
import { BUS_STREETS } from '../world/lowBorough/data';
import type { CityLayout, PointOfInterest } from '../world/types';

/**
 * Where an idle group stands at a POI (pub door, phone box, shop front). The naive spot — 1.9 m out
 * from the entrance — lands in the parking lane or the carriageway wherever the pavement is narrow
 * (POLICE-6, CITY-8) and once inside a warehouse. This searches near that spot for a circle that is
 * clear of every carriageway and junction box by a kerb margin and clear of buildings and static
 * colliders, so the group stands on the pavement interior or out on a plaza. Pure; runs once per POI
 * at load.
 */

/** Gap kept between a member's body edge and the kerb line (m). */
export const KERB_CLEARANCE = 0.6;
/** Body radius of a standing pedestrian (m). */
export const PED_BODY_R = 0.35;
/** Preferred spot: this far out from the entrance along the POI heading. */
const DOOR_OFFSET = 1.9;
/** How far from the preferred spot the search may move the group (m). */
const SEARCH_R = 5;
const SEARCH_STEP = 0.25;
/** Ring radii tried, widest first; a group on a narrow pavement huddles tighter (0.35: the 2.2 m arterial pavements). */
const RING_RADII = [0.75, 0.5, 0.35];

export interface IdleSpot {
  /** Group centre. */
  x: number;
  z: number;
  /** Ring radius members stand on (0 for a single ped). */
  ring: number;
}

/** Clearance (m) from `p` to the nearest carriageway edge or junction box; negative inside one. */
export function kerbClearance(layout: CityLayout, p: V2, edges = layout.roads.edges, ixs = layout.intersections): number {
  let best = Infinity;
  for (const e of edges) {
    const a = layout.roads.nodes[e.from]!.pos;
    const b = layout.roads.nodes[e.to]!.pos;
    const d = pointSegmentDistance(p, a, b) - e.width / 2;
    if (d < best) best = d;
  }
  for (const ix of ixs) {
    const d = Math.hypot(p.x - ix.pos.x, p.z - ix.pos.z) - ix.radius;
    if (d < best) best = d;
  }
  return best;
}

type Obstacle = { kind: 'obb'; c: V2; ax: V2; az: V2; hx: number; hz: number } | { kind: 'circle'; c: V2; r: number } | { kind: 'seg'; a: V2; b: V2; r: number };

/** Signed-ish clearance from `p` to an obstacle (0 or less = inside/touching). */
function obstacleClearance(o: Obstacle, p: V2): number {
  if (o.kind === 'circle') return Math.hypot(p.x - o.c.x, p.z - o.c.z) - o.r;
  if (o.kind === 'seg') return pointSegmentDistance(p, o.a, o.b) - o.r;
  const dx = p.x - o.c.x;
  const dz = p.z - o.c.z;
  const lx = Math.abs(dx * o.ax.x + dz * o.ax.z) - o.hx;
  const lz = Math.abs(dx * o.az.x + dz * o.az.z) - o.hz;
  if (lx <= 0 && lz <= 0) return Math.max(lx, lz);
  return Math.hypot(Math.max(lx, 0), Math.max(lz, 0));
}

/** Buildings and static colliders as obstacles (only those within `radius` of `near`). */
function obstaclesNear(layout: CityLayout, near: V2, radius: number): Obstacle[] {
  const out: Obstacle[] = [];
  const close = (c: V2, ext: number): boolean => Math.hypot(c.x - near.x, c.z - near.z) < radius + ext;
  for (const b of layout.buildings) {
    if (!close(b.center, Math.hypot(b.halfX, b.halfZ))) continue;
    const { ax, az } = obbAxes(b.angle);
    out.push({ kind: 'obb', c: b.center, ax, az, hx: b.halfX, hz: b.halfZ });
  }
  for (const s of layout.colliders) {
    if (s.kind === 'box') {
      if (!close(s.center, Math.hypot(s.halfX, s.halfZ))) continue;
      const { ax, az } = obbAxes(s.angle ?? 0);
      out.push({ kind: 'obb', c: s.center, ax, az, hx: s.halfX, hz: s.halfZ });
    } else if (s.kind === 'circle') {
      if (close(s.center, s.radius)) out.push({ kind: 'circle', c: s.center, r: s.radius });
    } else {
      const mid = { x: (s.a.x + s.b.x) / 2, z: (s.a.z + s.b.z) / 2 };
      if (close(mid, Math.hypot(s.b.x - s.a.x, s.b.z - s.a.z) / 2)) out.push({ kind: 'seg', a: s.a, b: s.b, r: (s.thickness ?? 0.6) / 2 });
    }
  }
  return out;
}

/**
 * Spot for an idle group of `size` at `poi`, or null when there is no pavement or plaza nearby wide
 * enough (the site then never forms a group). Deterministic: depends only on the layout.
 */
export function idleGroupSpot(layout: CityLayout, poi: PointOfInterest, size: number): IdleSpot | null {
  const fx = Math.sin(poi.heading);
  const fz = Math.cos(poi.heading);
  const pref = { x: poi.pos.x + fx * DOOR_OFFSET, z: poi.pos.z + fz * DOOR_OFFSET };
  const reach = SEARCH_R + 2;
  const edges = layout.roads.edges.filter((e) => {
    const a = layout.roads.nodes[e.from]!.pos;
    const b = layout.roads.nodes[e.to]!.pos;
    return pointSegmentDistance(pref, a, b) < reach + e.width / 2 + 2;
  });
  const ixs = layout.intersections.filter((ix) => Math.hypot(pref.x - ix.pos.x, pref.z - ix.pos.z) < reach + ix.radius + 2);
  const obstacles = obstaclesNear(layout, pref, reach + 2);
  const radii = size <= 1 ? [0] : RING_RADII;
  for (const ring of radii) {
    const needKerb = ring + PED_BODY_R + KERB_CLEARANCE;
    const needSolid = ring + PED_BODY_R + 0.1;
    let best: IdleSpot | null = null;
    let bestScore = Infinity;
    const n = Math.round(SEARCH_R / SEARCH_STEP);
    for (let i = -n; i <= n; i++) {
      for (let j = -n; j <= n; j++) {
        const ox = i * SEARCH_STEP;
        const oz = j * SEARCH_STEP;
        const off = Math.hypot(ox, oz);
        if (off > SEARCH_R) continue;
        // Nearest to the door wins, but keep facing it: a spot behind the entrance line costs extra.
        const along = ox * fx + oz * fz;
        const score = off + (along < -DOOR_OFFSET + 0.5 ? 2 : 0);
        if (score >= bestScore) continue;
        const p = { x: pref.x + ox, z: pref.z + oz };
        if (kerbClearance(layout, p, edges, ixs) < needKerb) continue;
        let blocked = false;
        for (const o of obstacles) {
          if (obstacleClearance(o, p) < needSolid) {
            blocked = true;
            break;
          }
        }
        if (blocked) continue;
        best = { x: p.x, z: p.z, ring };
        bestScore = score;
      }
    }
    if (best) return best;
  }
  return null;
}

/** Same hash as world/view/meshBuilder.ts `hash01` (kept here so the sim never imports the view). */
function hash01(a: number, b = 0, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}

export interface BusStopSite {
  poi: PointOfInterest;
  spot: IdleSpot;
  size: number;
  /** A point in the road the queue looks at. */
  face: V2;
}

/**
 * Where people wait for a bus: the stops world/view/streetLife.ts puts on the bus routes
 * (`BUS_STREETS`, two per long enough edge, the same hash skips some), first-choice position. The
 * queue stands back from the flag on the pavement and faces the road. Pure; runs once at load.
 */
export function busStopSites(layout: CityLayout): BusStopSite[] {
  const out: BusStopSite[] = [];
  const { nodes, edges } = layout.roads;
  for (const e of edges) {
    if (!BUS_STREETS.has(e.name) || e.alley || e.sidewalk < 1.5) continue;
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < 45 || hash01(e.id, 7, 13) < 0.35) continue;
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const l = { x: d.z, z: -d.x };
    for (const [tf, side] of [
      [0.32, 1],
      [0.7, -1],
    ] as const) {
      if (side === 1 && e.lanesForward === 0) continue;
      if (side === -1 && e.lanesBackward === 0) continue;
      const t = len * tf;
      const pole = { x: a.x + d.x * t + l.x * side * (e.width / 2 + 0.55), z: a.z + d.z * t + l.z * side * (e.width / 2 + 0.55) };
      // A pseudo-POI at the pole whose "door" faces away from the road: the queue stands on the pavement behind the flag.
      const heading = Math.atan2(l.x * side, l.z * side);
      const size = 2 + (hash01(e.id, side + 2, 5) < 0.4 ? 1 : 0);
      const poi: PointOfInterest = { id: `bus-${e.id}-${side}`, kind: 'landmark', name: 'Bus stop', pos: pole, heading, radius: 2, districtId: e.districtId };
      const spot = idleGroupSpot(layout, { ...poi, pos: { x: pole.x - Math.sin(heading) * 1.4, z: pole.z - Math.cos(heading) * 1.4 } }, size);
      if (!spot) continue;
      out.push({ poi, spot, size, face: { x: a.x + d.x * t, z: a.z + d.z * t } });
    }
  }
  return out;
}
